import {
  PaymentMethods,
  PaymentsStatuses,
  OrderStatuses,
  DeliveryMethods,
  SelfDeliveryMethods,
  ReceivingMethods,
  TcDeliveryTypes,
} from '@/enums';
import { OrganizationShared } from '@/modules/organizations/shared/types';
import {
  ApiPagination, RequestPagination, Response, SimpleEnumType, WareHouse,
} from '@/types';
import { ProductShared } from '@/modules/products/shared/types';
import { Products } from '@/modules/products/types';

interface CartPayloadItem {
  product_id?: number;
  product_code?: number | string;
  nomenclature?: string;
  quantity?: number | string;
  license_key?: string | null,
  banner_id?: string | unknown,
  page?: string | null
}

interface CartInfo {
  delivery_type: SimpleEnumType<DeliveryMethods | null>;
  tc_delivery_type: TcDeliveryTypes | null;
  tc_receiving_method: SimpleEnumType<ReceivingMethods | null>;
  self_delivery_type: SimpleEnumType<SelfDeliveryMethods | null>;
  delivery_address: {
    id: number;
    address: string;
    contact_person: string
  } | null;
  warehouse: WareHouse | null;
  transport_company_name: string | null;
  dellin_city: string | null;
  dellin_terminal: string | null;
  comment: string | null;
  is_express: boolean;
  is_dellin: boolean;
}

export namespace Cart {
  import CatalogProductItem = Products.CatalogProductItem;
  import ProductPrices = Products.ProductPrices;

  export enum Pages {
    CART = 'cart',
    DETAIL = 'cart-detail',
    CARTS_HISTORY = 'carts-history',

    CART_HISTORY_ALERT = 'cart_history'
  }
  export const StoragePrefix = 'cart';

  export interface CartItem {
    [key: string]: unknown;
    id: number;
    product: {
      id: number;
      product_code: string;
      nomenclature: string;
      license_keys: string[];
      related_products: ProductShared[] | null
    };
    quantity: number | string;
    prices: {
      rub: {
        value: number; // цена за 1 шт.
        total_value: number; // стоимость (цена * кол-во)
        total_nds_value: number; // сумма НДС
        /**
         * тоже самое, но строка в формате 0.00
         * не используется на фронте, т. к. есть фильтр currency_rub
         * @deprecated
         */
        total_nds_string_value: string;
        total_sale_value: number; // цена продажи (с учетом ндс и наценки)
      };
      usd: {
        value: number; // цена за 1 шт.
        total_value: number; // стоимость (цена * кол-во)
        total_sale_value: number; // цена продажи (с учетом ндс и наценки)
      };
      nds: number;
      markup: number; // наценка
    };
    payment_type: PaymentMethods;
    is_selected: boolean;
  }

  export interface CartTotal {
    prices: {
      nds_value: number; // итого (с ндс)
      nds_string_value: string; // итого (с ндс)в формате 0.00
      nds_value_usd: number | null; // итого (с ндс) в долларах
      no_nds_value: number; // общая цена без НДС
      /**
       * тоже самое, в формате 0.00
       * не используется на фронте, т. к. есть фильтр currency_rub
       * @deprecated
       */
      no_nds_string_value: string;
      selected_nds_value: number; // сумма (с ндс) выбранных продуктов
    };
    quantity: number;
    selected_quantity: number;
    show_atol_connect_notification: boolean;
    order_not_possible: boolean;
    is_only_software_autoshipment: boolean
    is_automatic_shipment: boolean
    balance_limit: number | null
    has_software_registry_items: boolean
  }

  export interface UpdateCartDTO {
    id: number;
    quantity: string | number;
    payment_type?: PaymentMethods;
    license_key?: string;
    is_selected: boolean;
  }

  export interface MultiplyDeleteDTO {
    items: Cart.CartItem[];
    warehouse_id: number | null;
    project_id: number | null;
    filters?: {
      search: string | null
    }
  }

  export interface CartPayload {
    warehouse_id: number | null;
    is_realization_contract: boolean;
    items: CartPayloadItem[];
  }

  export interface CartUpdPayloadBase {
    warehouse_id: number | null;
    project_id: number | null;
    filters?: {
      search: string | null
    }
  }

  export interface CartUpdPayload extends CartUpdPayloadBase {
    items: UpdateCartDTO[] | UpdateCartDTO;
  }

  export interface CartUpdPayloadProduct extends CartUpdPayloadBase {
    product: ProductShared;
  }

  export interface CartUpdPayloadProduct1 extends CartUpdPayloadBase {
    product: CatalogProductItem;
  }

  export interface CartFields {
    project_id: number | null,
    organization: number | null,
    warehouse: number | null,
    payment_type: string | null,
    delivery_type: string | null,
    products: [],
    delivery_address: number | null,
    transport_company_id: number | null,
    self_delivery_type: string | null,
    tc_delivery_type: string | null,
    tc_receiving_method: string | null,
    is_express: boolean,
    is_dellin: boolean,
    delline_place: number | null,
    delline_place_id: number | null,
    delline_terminal: number | null,
    delline_terminal_id: number | null,
    comment: string | null,
  }

  export type CartResponse = Response<{
    items: CartItem[];
    items_without_accreditation: CartItem[];
    total: CartTotal;
    project_id: number | null;
    popup_message: string | null;
    not_added_product_ids: string[];
    is_placing_order_not_available: boolean;
    is_cash_registers_auto_addition: boolean;
  }>;

  export interface EditLicenseDialog {
    [key: string]: unknown,
    visible: boolean,
    loading: boolean,
    license_key: string,
    item: Cart.CartItem
  }

  export interface DeleteDialog {
    [key: string]: unknown,
    visible: boolean,
    type: string,
    selected_products: CartItem[],
    is_last_element: boolean,
  }

  export interface AccreditationDialog {
    [key: string]: unknown,
    visible: boolean,
    selected_products: CartItem[],
    loading: false,
    warehouse_id: 0,
    project_id: 0
  }

  export interface CatalogProduct {
    [key: string]: unknown,
    id: number,
    category_id: number,
    category_name: string,
    product_code: string,
    nomenclature: string,
    license_keys?: string[], // добавляется на фронте
    quantity?: number | string, // добавляется на фронте
    prices: ProductPrices,
    residues: string | null,
    post_prod_residues: string | null,
    is_required_license: boolean,
    related_products: Array<CatalogProduct>, // TODO выделить в отдельный интерфейс после отвязки всех роутов по получению списка товаров
    software_type_name: string | null,
    is_allowed_by_realization: boolean,
  }

  export interface CatalogProductResponse extends ApiPagination {
    data: {
      items: CatalogProduct[],
      filters: {
        categories: Products.CategoryFilterItem[],
        software_types: Products.CatalogFilterItem[],
        equipment_models: Products.EquipmentModelFilterItem[]
      }
    }
  }

  export type CatalogProductRequestFilters = {
    [key: string]: unknown,
    search: string,
    categories: number[];
    software_type: number | null;
    equipment_models: number[],
    in_stock: boolean,
    is_favorite_only: boolean,
    is_allowed_by_realization: boolean,
    warehouse_id: number | null
  }

  export type CatalogProductRequest = {
    filters: CatalogProductRequestFilters,
    filters_exist: boolean,
    pagination: RequestPagination,
  }
}

export interface CartHistoryItem {
  id: number;
  date: string;
  quantity: number;
  organization: string;
  discount: number;
  price: number;
  is_project: boolean;
  has_unread_change: boolean | null;
  is_external_api: boolean;
}

export interface RetryCartInterface {
  id: number;
  is_project: boolean;
}

export interface FiltersOrganization {
  [key: string]: string;
}

export interface UsersFilter {
  id: string | number,
  name: string;
}

export interface HistroyCartsFilters {
  [key: string]: unknown;
  search: string;
  date: string | null;
  organization_id: string | null;
  user_ids: UsersFilter[];
  only_has_unread_changes: boolean;
}

export interface CartDetailProduct {
  [key: string]: unknown;
  id: number;
  nomenclature: string;
  order_id: number | null;
  quantity: number;
  product_code: string;
  order_status: SimpleEnumType<OrderStatuses> | null;
  order_payment_status: SimpleEnumType<PaymentsStatuses> | null;
  organization_id: string;
  number_realization: string | null; // номер реализации
  reason: null | string; // причина, по которой товар не вошел в заказ
  discount: number; // скидка
  total_price_rub: number; // сумма
  price_rub: number; // цена за 1 шт.
  price_usd: number; // цена за 1 шт.
  product: {
    id: number;
    product_code: string;
    nomenclature: string;
  };
}

export interface CartDetailOrder {
  id: number;
  project_id: number | null;
  quantity: number;
  price: number;
  order_status: SimpleEnumType<OrderStatuses>;
  belongs_to_set: boolean,
}

export interface CartDetail {
  id: number;
  external_id: string;
  created_at: string;
  discount: number;
  price: number;
  products: CartDetailProduct[];
  orders: CartDetailOrder[];
  external_number?: number | null;
  info?: CartInfo;
  is_project: boolean;
  is_external_api: boolean;
  external_api_cart_id: string | null;
}

export interface CartFileUploadData {
  fail_rows: number | null;
  success_rows: number | null;
  total_rows: number | null;
}

export type CartOrganization = Pick<OrganizationShared, 'id' | 'name_organization'> & {
  is_deferment_payment: boolean; // есть ли у компании форма оплаты "с отсрочкой"
  is_insured: boolean;
  balance_credit_limit: string | null;
  has_realization_contract: boolean; // признак наличия активного договора реализации
  inn_organization: string;
  kpp_organization: string;
};

export interface CartProject {
  id: number;
  name: string;
}

export namespace DelLine {
  export interface DelLinePlace {
    city_id: number;
    name: string;
  }

  export interface DelLineTerminal {
    id: number;
    address: string;
  }
}

export interface CartFilters {
  [key: string]: unknown;
  search: string;
}
